"use client";

import React from "react";
import { MoonStar, BatteryWarning, Brain, Activity, ShieldAlert, HeartPulse } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const symptoms = [{
  id: 1,
  title: "Loud & Persistent Snoring",
  description: "Often the first sign noticed by partners, characterized by loud, frequent disruptions in breathing patterns throughout the night.",
  icon: MoonStar
}, {
  id: 2,
  title: "Excessive Daytime Sleepiness",
  description: "Struggling to stay awake during meetings or while driving, despite spending what feels like enough hours in bed.",
  icon: BatteryWarning
}, {
  id: 3,
  title: "Morning Headaches & Fog",
  description: "Waking up with a dull ache or feeling mentally 'cloudy' due to low oxygen levels during your sleep cycle.",
  icon: Brain
}, {
  id: 4,
  title: "Gasping or Choking",
  description: "Episodes where you wake up suddenly gasping for air, which is a clear indicator of a blocked or collapsed airway.",
  icon: Activity
}, {
  id: 5,
  title: "High Blood Pressure",
  description: "Untreated sleep apnea is a leading contributor to hypertension and puts significant stress on your cardiovascular system.",
  icon: ShieldAlert
}, {
  id: 6,
  title: "Restless Nighttime Sleep",
  description: "Frequent tossing, turning, or waking up multiple times, preventing your body from reaching deep, restorative sleep stages.",
  icon: HeartPulse
}];
export default function TheSleepGap() {
  const headerReveal = useRevealOnScroll({
    threshold: 0.1
  });
  const gridReveal = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="99171" id="signs-you-need-help" className="bg-background text-foreground relative overflow-hidden py-24 lg:py-32">
      {}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-[20%] -left-[10%] h-[70%] w-[50%] animate-pulse rounded-full bg-primary/5 blur-[120px] duration-[8000ms]" />
        <div className="absolute top-[40%] -right-[20%] h-[80%] w-[60%] animate-pulse rounded-full bg-chart-3/5 blur-[150px] duration-[12000ms]" />
        <div className="absolute -bottom-[30%] left-[20%] h-[60%] w-[50%] animate-pulse rounded-full bg-primary/10 blur-[100px] duration-[10000ms]" />
      </div>

      <div className="container relative z-10 mx-auto px-4 md:px-6">
        {}
        <div ref={headerReveal.ref} className={`mx-auto max-w-3xl text-center transition-all duration-1000 ${headerReveal.isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
          <h2 className="font-sans text-4xl font-bold tracking-tight text-secondary sm:text-5xl md:text-6xl">
            Signs You Need Help
          </h2>
          <p className="text-muted-foreground mt-6 text-lg leading-relaxed md:text-xl">
            Living with untreated sleep apnea in Los Angeles can affect your health, safety, and productivity. Recognizing these common symptoms is the first step toward reclaiming your life.
          </p>
        </div>

        {}
        <div ref={gridReveal.ref} className={`mt-16 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 transition-all duration-1000 delay-200 ${gridReveal.isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
          {symptoms.map((symptom, i) => {
          const Icon = symptom.icon;
          return <div data-index={i} key={symptom.id} className="group h-full">
                <Card className="bg-card border-border hover:border-primary/20 hover:shadow-xl hover:-translate-y-2 relative h-full overflow-hidden transition-all duration-500 rounded-2xl">
                  {}
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/0 via-primary/0 to-primary/5 opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                  
                  <CardHeader className="pb-4">
                    <div className="bg-chart-3/10 text-chart-3 mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl transition-transform duration-500 group-hover:scale-110 group-hover:rotate-12">
                      <Icon className="h-6 w-6" />
                    </div>
                    <CardTitle className="text-xl tracking-tight text-secondary font-bold">
                      {symptom.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-muted-foreground text-base leading-relaxed">
                      {symptom.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              </div>;
        })}
        </div>

        {}
        <div className="mt-16 flex justify-center">
          <Button asChild size="lg" className="bg-chart-2 hover:bg-chart-2/90 text-white rounded-full px-8 py-6 text-lg shadow-lg transition-all hover:shadow-xl">
            <Link to="https://app.dentulu.com/sleep-apnea/questionnaire">
              Take the Sleep Quiz
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}